/**
 * Door assembly renderer.
 *
 * Layout owns millimetre bounds, including threshold and cill.
 * Paint order: calculated frame base → nine-slice profile runs → slab → threshold → cill.
 * Internal view applies a horizontal mirror to shared geometry unless a component override is registered.
 *
 * Profile artwork is inlined in the same SVG document and cropped via nested
 * viewBox + `<use>`. An external `<image href="*.svg">` cannot crop source
 * coordinates; browsers meet-fit the whole original-width drawing instead.
 */
import React, { useId, useMemo } from 'react';
import { colourIdForViewSide } from '../colour/viewSideColours';
import type { DoorViewSide } from '../surfaces/doorViewSide';
import type { DoorConfiguration } from '../types/doorConfiguration';
import {
  getFrameProfileAsset,
  shouldOverrideFrameProfilePalette,
} from '../assets/frameProfileAssets';
import {
  BUTT_HEAD_SLICE_NAMES,
  BUTT_HEAD_SLICES,
  getFrameProfileNineSlice,
  type FrameProfileSliceDefinition,
  type FrameProfileStretch,
} from '../assets/frameProfileSlices';
import { buildFrameColourPaletteCss } from '../colour/frameColourPaletteCss';
import { generateComponentColourPalettes } from '../colour/generateTonePalette';
import { resolveRenderHex } from '../colour/productColours';
import { useSvgAssetWithPalette } from '../colour/useSvgAssetWithPalette';
import { computeDoorLayout, type RectMm } from '../layout/doorLayout';
import { ExternalSlabRenderer } from '../surfaces/external/ExternalSlabRenderer';
import { usesSharedViewGeometry } from './componentViewOverride';
import {
  computeFrameNineSliceDestinations,
  getFrameCornerPaintOrder,
  getFrameRunPaintOrder,
} from './frameNineSliceLayout';
import { resolveFrameProfileRenderSpec } from './frameProfileRenderSpec';
import { horizontalMirrorTransform } from './viewSideTransform';

/** Cill outline in millimetres. Inset so the full stroke stays inside layout bounds. */
const CILL_EDGE_STROKE_MM = 3;

function rectProps(bounds: RectMm) {
  return {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

function insetRect(bounds: RectMm, insetMm: number): RectMm {
  return {
    x: bounds.x + insetMm,
    y: bounds.y + insetMm,
    width: bounds.width - insetMm * 2,
    height: bounds.height - insetMm * 2,
  };
}

function preserveAspectRatioFor(stretch: FrameProfileStretch): string {
  return stretch === 'none' ? 'xMidYMid meet' : 'none';
}

function FrameProfileSlice(props: {
  slice: FrameProfileSliceDefinition;
  dest: RectMm;
  assetViewBox: { x: number; y: number; width: number; height: number };
  spriteId: string | null;
}) {
  const { slice, dest, assetViewBox, spriteId } = props;
  const source = slice.sourceBounds;
  return (
    <svg
      data-part={`frame-slice-${slice.name}`}
      data-stretch={slice.stretch}
      x={dest.x}
      y={dest.y}
      width={dest.width}
      height={dest.height}
      viewBox={`${source.x} ${source.y} ${source.width} ${source.height}`}
      preserveAspectRatio={preserveAspectRatioFor(slice.stretch)}
      overflow="hidden"
    >
      {spriteId ? (
        <use
          href={`#${spriteId}`}
          xlinkHref={`#${spriteId}`}
          x={assetViewBox.x}
          y={assetViewBox.y}
          width={assetViewBox.width}
          height={assetViewBox.height}
        />
      ) : null}
    </svg>
  );
}

export interface DoorSvgRendererProps {
  configuration: DoorConfiguration;
  viewSide?: DoorViewSide;
  /** Visible millimetre window. Defaults to the full-door fit (`overallBounds`). */
  viewBox?: RectMm;
  debugHardwareGuides?: boolean;
}

export const DoorSvgRenderer: React.FC<DoorSvgRendererProps> = ({
  configuration,
  viewSide = 'external',
  viewBox,
  debugHardwareGuides = false,
}) => {
  const spriteUid = useId();
  const layout = computeDoorLayout(configuration);
  const { overallBounds, frameBounds, slabOpeningBounds, thresholdBounds, cillBounds } = layout;
  const renderSpec = resolveFrameProfileRenderSpec(configuration.frameProfile);
  const frameJoint = renderSpec.joint;
  const asset = getFrameProfileAsset(renderSpec.artworkProfile);
  const nineSlice = getFrameProfileNineSlice(renderSpec.artworkProfile);
  const destinations = computeFrameNineSliceDestinations(
    frameBounds,
    slabOpeningBounds,
    nineSlice,
    frameJoint
  );
  const frameRunPaintOrder = getFrameRunPaintOrder(frameJoint);
  const frameCornerPaintOrder = getFrameCornerPaintOrder(frameJoint);
  const frameBaseMembers =
    frameJoint === 'butt'
      ? [
          { part: 'door-frame-jamb-left', bounds: destinations.leftJamb },
          { part: 'door-frame-jamb-right', bounds: destinations.rightJamb },
          { part: 'door-frame-head', bounds: destinations.head },
        ]
      : [
          { part: 'door-frame-head', bounds: destinations.head },
          { part: 'door-frame-jamb-left', bounds: destinations.leftJamb },
          { part: 'door-frame-jamb-right', bounds: destinations.rightJamb },
        ];
  const frameBaseHex = resolveRenderHex(colourIdForViewSide(configuration, 'frame', viewSide));
  const cillHex = resolveRenderHex(colourIdForViewSide(configuration, 'cill', viewSide));
  const thresholdHex = resolveRenderHex(colourIdForViewSide(configuration, 'threshold', viewSide));
  const slabColourId = colourIdForViewSide(configuration, 'slab', viewSide);
  const viewTransform =
    viewSide === 'internal' ? horizontalMirrorTransform(overallBounds.width) : undefined;
  const palettes = useMemo(
    () =>
      generateComponentColourPalettes({
        frameBaseHex,
        cillHex,
        thresholdBaseHex: thresholdHex,
      }),
    [cillHex, frameBaseHex, thresholdHex]
  );
  const paletteCss = useMemo(
    () =>
      shouldOverrideFrameProfilePalette(asset, frameBaseHex)
        ? buildFrameColourPaletteCss(palettes)
        : null,
    [asset, frameBaseHex, palettes]
  );
  const profileMarkup = useSvgAssetWithPalette(
    asset.publicPath,
    asset.contentVersion,
    renderSpec.artworkProfile,
    paletteCss
  );
  const spriteId = `door-frame-profile-sprite-${spriteUid.replace(/:/g, '')}`;
  const visible = viewBox ?? overallBounds;

  return (
    <svg
      data-part="door-preview"
      data-profile-asset={asset.publicPath}
      data-frame-joint={frameJoint}
      role="img"
      data-view-side={viewSide}
      aria-label={`Configurable door preview, ${configuration.frameProfile} frame profile${
        viewSide === 'internal' ? ', internal view' : ''
      }`}
      width={overallBounds.width}
      height={overallBounds.height}
      viewBox={`${visible.x} ${visible.y} ${visible.width} ${visible.height}`}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      {profileMarkup ? (
        <defs>
          <symbol
            id={spriteId}
            data-part="door-frame-profile-sprite"
            viewBox={`${asset.viewBox.x} ${asset.viewBox.y} ${asset.viewBox.width} ${asset.viewBox.height}`}
            dangerouslySetInnerHTML={{ __html: profileMarkup }}
          />
        </defs>
      ) : null}
      <g
        data-part="door-view"
        data-view-side={viewSide}
        transform={viewTransform}
      >
        {usesSharedViewGeometry('frame', viewSide) ? (
          <g data-part="door-frame" data-joint={frameJoint}>
            {frameBaseMembers.map(({ part, bounds }) => (
              <rect
                key={part}
                data-part={part}
                {...rectProps(bounds)}
                fill={palettes.frame.base}
              />
            ))}
            {frameRunPaintOrder.map((name) => (
              <FrameProfileSlice
                key={name}
                slice={nineSlice.slices[name]}
                dest={destinations.slices[name]}
                assetViewBox={asset.viewBox}
                spriteId={profileMarkup ? spriteId : null}
              />
            ))}
            {frameJoint === 'butt'
              ? BUTT_HEAD_SLICE_NAMES.map((name) => (
                  <FrameProfileSlice
                    key={name}
                    slice={BUTT_HEAD_SLICES[name]}
                    dest={destinations.buttHeadSlices[name]}
                    assetViewBox={asset.viewBox}
                    spriteId={profileMarkup ? spriteId : null}
                  />
                ))
              : null}
            {frameCornerPaintOrder.map((name) => (
              <FrameProfileSlice
                key={name}
                slice={nineSlice.slices[name]}
                dest={destinations.slices[name]}
                assetViewBox={asset.viewBox}
                spriteId={profileMarkup ? spriteId : null}
              />
            ))}
          </g>
        ) : null}
        {usesSharedViewGeometry('slab', viewSide) ? (
          <ExternalSlabRenderer
            slab={configuration.slab}
            slabOpeningBounds={slabOpeningBounds}
            viewSide={viewSide}
            colourId={slabColourId}
            glazing={configuration.glazing}
            hardware={configuration.hardware.external}
            debugHardwareGuides={debugHardwareGuides}
          />
        ) : null}
        {thresholdBounds && usesSharedViewGeometry('threshold', viewSide) ? (
          <g data-part="door-threshold">
            <rect {...rectProps(thresholdBounds)} fill={palettes.threshold.base} />
          </g>
        ) : null}
        {cillBounds && usesSharedViewGeometry('cill', viewSide) ? (
          <g data-part="door-cill">
            <rect {...rectProps(cillBounds)} fill={palettes.cill.base} />
            <rect
              data-part="door-cill-edge"
              {...rectProps(insetRect(cillBounds, CILL_EDGE_STROKE_MM / 2))}
              fill="none"
              stroke={palettes.cill.shadow}
              strokeWidth={CILL_EDGE_STROKE_MM}
            />
          </g>
        ) : null}
      </g>
    </svg>
  );
};
